import axios from '@core/axios/axios.js'
import API from '@core/api.js'

const ORDER_COMPLETE_ID_KEY = 'order-complete-order-id'
const ORDER_WELCOME_ID_KEY = 'order-welcome-order-id'

function storeCompleteOrderId(orderId) {
  sessionStorage.setItem(ORDER_COMPLETE_ID_KEY, String(orderId))
}

export function rememberCompleteOrderId(orderId) {
  storeCompleteOrderId(orderId)
}

function consumeStoredCompleteOrderId() {
  const orderId = sessionStorage.getItem(ORDER_COMPLETE_ID_KEY)
  if (orderId) {
    sessionStorage.removeItem(ORDER_COMPLETE_ID_KEY)
    return orderId
  }
  return null
}

function storeWelcomeOrderId(orderId) {
  sessionStorage.setItem(ORDER_WELCOME_ID_KEY, String(orderId))
}

function consumeStoredWelcomeOrderId() {
  const orderId = sessionStorage.getItem(ORDER_WELCOME_ID_KEY)
  if (orderId) {
    sessionStorage.removeItem(ORDER_WELCOME_ID_KEY)
    return orderId
  }
  return null
}

/**
 * Returns true when the client has exactly one completed order and it
 * matches the order that was just paid.
 */
export async function isFirstCompletedOrder(orderId) {
  const response = await axios.get(API.getOrdersResource, {
    params: { page: 1, perPage: 2 },
  })

  const orders = response.data?.data ?? []
  const total = response.data?.meta?.total ?? orders.length

  // No completed orders yet (e.g. PayPal capture still propagating).
  if (total === 0) {
    return true
  }

  return total === 1 && Number(orders[0]?.id) === Number(orderId)
}

/**
 * Navigate to the order-complete page after payment. First-time buyers
 * land on /order/welcome/ (v1 parity for GTM); returning customers go
 * to /order/complete.
 */
export async function navigateAfterPayment(router, orderId) {
  const isFirstOrder = await isFirstCompletedOrder(orderId)

  if (isFirstOrder) {
    // Carry the just-paid order id to the welcome page. Async gateways
    // (PayPal) may not have the order in the completed-orders list yet, so the
    // welcome page must not re-derive it from that list or the first-order CJ
    // conversion is silently lost while the capture webhook propagates.
    storeWelcomeOrderId(orderId)
    return router.push({ name: 'order-welcome' })
  }

  storeCompleteOrderId(orderId)
  return router.push({ name: 'order-complete' })
}

/**
 * Resolve the order id for the complete page. Prefers the id stored
 * during post-payment navigation, then falls back to the most recently
 * completed order.
 */
export async function resolveCompleteOrderId() {
  const stored = consumeStoredCompleteOrderId()
  if (stored) {
    return stored
  }

  const response = await axios.get(API.getOrdersResource, {
    params: { page: 1, perPage: 1 },
  })

  const orders = response.data?.data ?? []
  return orders[0]?.id ?? null
}

/**
 * Resolve the order id for the welcome page. Prefers the id stored during
 * post-payment navigation (race-free for async gateways), then falls back to
 * inferring it from the completed-orders list for direct navigations. The
 * fallback returns null when the client has other orders.
 */
export async function resolveWelcomeOrderId() {
  const stored = consumeStoredWelcomeOrderId()
  if (stored) {
    return stored
  }

  const response = await axios.get(API.getOrdersResource, {
    params: { page: 1, perPage: 2 },
  })

  const orders = response.data?.data ?? []
  const total = response.data?.meta?.total ?? orders.length

  if (total !== 1 || !orders[0]?.id) {
    return null
  }

  return orders[0].id
}
